"use client";

import { cn } from "@/utils/utils";
import { captureClientEvent } from "@/lib/telemetry-client";
import Image from "next/image";
import { useState } from "react";
import { FaMapLocation, FaPlus, FaMinus } from "react-icons/fa6";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import karte5xl from "@/components/gfx/karte-5xl.jpg";
import karte4xl from "@/components/gfx/karte-4xl.jpg";
import karte3xl from "@/components/gfx/karte-3xl.jpg";
import karte2xl from "@/components/gfx/karte-2xl.jpg";
import karteXL from "@/components/gfx/karte-xl.jpg";
import karteLG from "@/components/gfx/karte-lg.jpg";
import karteMD from "@/components/gfx/karte-md.jpg";
import karteSM from "@/components/gfx/karte-sm.jpg";
import anfahrt01 from "@/components/gfx/anfahrt-01.jpg";
import anfahrt02 from "@/components/gfx/anfahrt-02.jpg";

const images = [
    karte5xl,
    karte4xl,
    karte3xl,
    karte2xl,
    karteXL,
    karteLG,
    karteMD,
    karteSM,
    anfahrt01,
    anfahrt02,
];

export default function Map({ className }: { className?: string }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const { ref, inView } = useInView({
        threshold: 0.2,
        triggerOnce: true
    });

    const handleZoomIn = () => {
        if (currentIndex < images.length - 1) {
            captureClientEvent("map_zoom_changed", {
                direction: "in",
                from_index: currentIndex,
                to_index: currentIndex + 1,
            });
            setCurrentIndex((prevIndex) => prevIndex + 1);
        }
    };

    const handleZoomOut = () => {
        if (currentIndex > 0) {
            captureClientEvent("map_zoom_changed", {
                direction: "out",
                from_index: currentIndex,
                to_index: currentIndex - 1,
            });
            setCurrentIndex((prevIndex) => prevIndex - 1);
        }
    };

    return (
        <motion.div 
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className={cn(
                "group relative h-fit w-fit max-w-4xl overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/30",
                className
            )}
        >
            {/* Map container */}
            <div className="relative">
                {images.map((image, index) => (
                    <div 
                        className={cn(
                            "transition-all duration-500",
                            index === currentIndex 
                                ? "relative z-10 h-auto w-auto" 
                                : "absolute left-0 top-0 z-0 h-full w-full opacity-0"
                        )} 
                        key={index}
                    >
                        <Image
                            src={image}
                            alt="Karte Stellplatz Zingst"
                            width={800}
                            height={600}
                            style={{ height: "auto" }}
                            className="rounded-3xl"
                            onDoubleClick={handleZoomIn}
                            priority={index < 3}
                        />
                    </div>
                ))}

                {/* Gradient overlay for depth */}
                <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
            </div>

            {/* Controls */}
            <div className="absolute bottom-6 right-6 z-20 flex flex-col items-center gap-3">
                <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleZoomIn} 
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 text-slate-700 shadow-lg backdrop-blur-sm transition-colors hover:bg-white"
                    disabled={currentIndex >= images.length - 1}
                >
                    <FaPlus className="h-5 w-5" />
                </motion.button>
                
                <motion.button 
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleZoomOut} 
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 text-slate-700 shadow-lg backdrop-blur-sm transition-colors hover:bg-white"
                    disabled={currentIndex <= 0}
                >
                    <FaMinus className="h-5 w-5" />
                </motion.button>

                <a 
                    href="https://www.google.com/maps/dir//Wohnmobilstellplatz+Zingst+Ostsee,+M%C3%BCggenburger+Weg+45a,+18374+Zingst,+Germany/@54.4411231,12.7042972,17z" 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => captureClientEvent("map_directions_clicked", { provider: "google_maps" })}
                >
                    <motion.button 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400 text-slate-900 shadow-lg transition-colors hover:bg-amber-300"
                    >
                        <FaMapLocation className="h-5 w-5" />
                    </motion.button>
                </a>
                
                <span className="rounded-lg bg-amber-400 px-2 py-1 text-xs font-medium text-slate-900 shadow-md">
                    Google
                </span>
            </div>

            {/* Zoom level indicator */}
            <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2 rounded-xl bg-white/90 px-4 py-2 shadow-lg backdrop-blur-sm">
                <span className="text-xs font-medium text-slate-500">Zoom</span>
                <div className="flex gap-1">
                    {images.slice(0, 6).map((_, i) => (
                        <div 
                            key={i}
                            className={cn(
                                "h-1.5 w-1.5 rounded-full transition-all",
                                i <= currentIndex ? "bg-amber-400" : "bg-slate-200"
                            )}
                        />
                    ))}
                </div>
            </div>
        </motion.div>
    );
}
